import { BankDocumentDto, BankDocumentEntryDto } from '../Types/bankDocument.types';
import { BankDocumentEntryModel, BankDocumentModel } from '../Model/bankDocument.model';
import { buildBankDocumentAllocationSummary } from '../Helpers/allocationSummary';

export const mapBankDocumentEntryDtoToModel = (
  entry: BankDocumentEntryDto,
  parentCurrency: string = 'PLN'
): BankDocumentEntryModel => {
  const allocations = (entry.allocations ?? [])
    .map((allocation) => ({
      id: allocation.id,
      targetType: allocation.targetType,
      targetId: allocation.targetId,
      allocatedAmount: allocation.allocatedAmount,
      sortOrder: allocation.sortOrder,
      invoicePaymentId: allocation.invoicePaymentId,
      reservationPaymentId: allocation.reservationPaymentId,
      reservationApplicantName: allocation.reservationApplicantName,
    }))
    .sort((left, right) => left.sortOrder - right.sortOrder || left.id - right.id);
  const allocationSummary = buildBankDocumentAllocationSummary({
    amount: entry.amount,
    invoiceId: entry.invoiceId,
    reservationId: entry.reservationId,
    allocations,
    allocationCount: entry.allocationCount,
    allocatedAmount: entry.allocatedAmount,
    remainingAmount: entry.remainingAmount,
    allocationTypeSummary: entry.allocationTypeSummary,
  });

  return {
    id: entry.id,
    bankDocumentId: entry.bankDocumentId,
    bookingDate: entry.bookingDate,
    valueDate: entry.valueDate,
    amount: entry.amount,
    currency: entry.currency ?? parentCurrency,
    title: entry.title,
    counterpartyName: entry.counterpartyName,
    counterpartyAccountNumber: entry.counterpartyAccountNumber,
    reference: entry.reference,
    invoiceId: entry.invoiceId,
    reservationId: entry.reservationId,
    expenseInvoiceId: entry.expenseInvoiceId,
    isPosted: entry.isPosted,
    postedAtUtc: entry.postedAtUtc,
    postedByUserId: entry.postedByUserId,
    invoicePaymentId: entry.invoicePaymentId,
    reservationPaymentId: entry.reservationPaymentId,
    allocations,
    allocationCount: allocationSummary.allocationCount,
    allocatedAmount: allocationSummary.allocatedAmount,
    remainingAmount: allocationSummary.remainingAmount,
    allocationTypeSummary: allocationSummary.allocationTypeSummary,
    hasPostingTarget: Boolean(
      entry.invoiceId ||
        entry.reservationId ||
        entry.expenseInvoiceId ||
        allocationSummary.allocationCount > 0
    ),
  };
};

export const mapBankDocumentDtoToModel = (document: BankDocumentDto): BankDocumentModel => {
  const currency = document.currency ?? 'PLN';
  const entries = document.entries.map((entry) => mapBankDocumentEntryDtoToModel(entry, currency));
  const entriesCount = document.transactionsCount ?? document.entriesCount ?? entries.length;
  const postedEntriesCount = entries.filter((entry) => entry.isPosted).length;

  return {
    id: document.id,
    fileName: document.fileName,
    accountNumber: document.accountNumber,
    importedAtUtc: document.importedAtUtc,
    importedByUserId: document.importedByUserId,
    entriesCount,
    postedEntriesCount,
    unpostedEntriesCount: entries.length - postedEntriesCount,
    bankAccountId: document.bankAccountId,
    companyId: document.companyId,
    entries,
  };
};

export const mapBankDocumentsDtoToModel = (documents: BankDocumentDto[]): BankDocumentModel[] =>
  documents.map(mapBankDocumentDtoToModel);
